import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { BAY_COLOR_PALETTE, BAY_DEFAULTS, MAX_BOOKING_HOURS_OPTIONS, SLOT_DURATION_OPTIONS } from "@/lib/constants";
import { createBay, getClientBufferFlag, updateBay } from "@/lib/api";
import { OpenDaysPicker } from "@/components/OpenDaysPicker";
import { DailyBreaksEditor } from "@/components/DailyBreaksEditor";
import HelpTip from "@/components/HelpTip";
import { AuTimeInput } from "@/components/AuDateTime";

/**
 * BayForm — create + edit dialog for lbs_bays.
 *
 * Props:
 *   open / onOpenChange
 *   siteId — parent site (required for create; ignored on edit)
 *   bay — existing row (edit) or null (create)
 *   nextSortOrder — auto-suggest sort_order on create
 *   onSaved(savedBay)
 */
export default function BayForm({ open, onOpenChange, siteId, site, bay, nextSortOrder, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bay-form-dialog">
        {open && (
          <BayFormInner
            key={bay?.id || "new"}
            siteId={siteId}
            site={site}
            bay={bay}
            nextSortOrder={nextSortOrder}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function BayFormInner({ siteId, site, bay, nextSortOrder, onOpenChange, onSaved }) {
  // Treat a bay with no id (e.g. duplicate-source pre-fill) as new.
  const isEdit = !!bay?.id;

  // Placeholder helper: show the inherited site value if set, otherwise "Inherit (no limit)".
  const ph = (v, suffix = "", fallback = "no limit") =>
    v === null || v === undefined || v === ""
      ? `Inherit (${fallback})`
      : `Inherit (${v}${suffix})`;

  // One-line summary of the site's current policy for the section subhead.
  const policyHints = (s) => {
    const parts = [];
    if (s.min_booking_minutes != null) parts.push(`min ${s.min_booking_minutes}m`);
    if (s.max_booking_hours   != null) parts.push(`max ${s.max_booking_hours}h`);
    if (s.min_lead_time_hours != null) parts.push(`lead ${s.min_lead_time_hours}h`);
    if (s.max_horizon_days    != null) parts.push(`horizon ${s.max_horizon_days}d`);
    if (s.max_concurrent_bookings != null) parts.push(`${s.max_concurrent_bookings}x concurrent`);
    if (s.requires_approval) parts.push("approval required");
    if (s.allow_multiday === false) parts.push("no multi-day");
    if (s.requires_check_in_out) parts.push("check-in/out");
    return parts.length ? parts.join(", ") : "no limits set";
  };

  const [values, setValues] = useState(() => ({
    ...BAY_DEFAULTS,
    sort_order: nextSortOrder ?? 0,
    ...(bay || {}),
  }));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // PR-1 buffer feature: show override field only when the parent client has
  // the master switch on. Also surface the site default as a placeholder hint.
  const [clientBufferEnabled, setClientBufferEnabled] = useState(false);
  const [siteDefaultBuffer, setSiteDefaultBuffer] = useState(0);

  useEffect(() => {
    if (!site?.client_id) return undefined;
    let cancelled = false;
    (async () => {
      const flag = await getClientBufferFlag(site.client_id);
      if (!cancelled) setClientBufferEnabled(flag.enabled);
    })();
    return () => { cancelled = true; };
  }, [site?.client_id]);

  useEffect(() => {
    if (site?.default_buffer_minutes != null) {
      setSiteDefaultBuffer(parseInt(site.default_buffer_minutes, 10) || 0);
    }
  }, [site?.default_buffer_minutes]);

  const set = (k, v) => setValues((vs) => ({ ...vs, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!values.name?.trim()) {
      setError("Name is required");
      return;
    }
    if (!!values.operating_hours_start !== !!values.operating_hours_end) {
      setError("Set both bay opening and closing times, or leave both blank to use the site's hours.");
      return;
    }
    // Coerce numeric strings → numbers (form inputs return strings)
    const payload = { ...values };
    ["max_vehicle_length", "max_vehicle_height", "max_vehicle_weight"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
        payload[k] = null;
      } else {
        const n = parseFloat(payload[k]);
        payload[k] = Number.isFinite(n) ? n : null;
      }
    });
    ["max_booking_hours", "sort_order", "slot_duration_minutes",
     "min_booking_minutes", "min_lead_time_hours", "max_horizon_days",
     "max_concurrent_bookings", "booker_min_duration_minutes",
     "buffer_minutes"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null) payload[k] = null;
      else payload[k] = parseInt(payload[k], 10);
    });
    // Strip buffer_minutes when the client has the feature off → prevents
    // "column does not exist" errors against pre-migration-150 DBs.
    if (!clientBufferEnabled) {
      delete payload.buffer_minutes;
    }
    // Tri-state boolean override: 'inherit' = null, 'true'/'false' = boolean
    if (payload.requires_check_in_out === "inherit" || payload.requires_check_in_out === "") {
      payload.requires_check_in_out = null;
    } else if (typeof payload.requires_check_in_out === "string") {
      payload.requires_check_in_out = payload.requires_check_in_out === "true";
    }

    setSaving(true);
    const { data, error: err } = isEdit
      ? await updateBay(bay.id, payload)
      : await createBay(siteId, payload);
    setSaving(false);

    if (err) {
      setError(err.message);
      return;
    }
    toast.success(isEdit ? "Bay updated" : "Bay created");
    onSaved?.(data);
    onOpenChange(false);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit bay" : "Add bay"}</DialogTitle>
        <DialogDescription>
          Bays are individual loading docks within a site. Drivers book a specific bay.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <Alert variant="destructive" data-testid="bay-form-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Identity</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="bay-name">Name *</Label>
                <Input
                  id="bay-name"
                  data-testid="bay-name-input"
                  value={values.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Bay 1"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Code</Label>
                <Input
                  data-testid="bay-code-input"
                  value={values.code || ""}
                  onChange={(e) => set("code", e.target.value.toUpperCase())}
                  placeholder="KYA-1"
                  maxLength={20}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                rows={2}
                data-testid="bay-description-input"
                value={values.description || ""}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Refrigerated, fork-accessible…"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Driver location instructions</Label>
              <Textarea
                rows={2}
                data-testid="bay-location-input"
                value={values.location_instructions || ""}
                onChange={(e) => set("location_instructions", e.target.value)}
                placeholder="Enter via Gate 3 and follow blue line to dock"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label>Persistent booking note (shown as a banner on every booking)</Label>
                <HelpTip id="bay-note">
                  Use this for bay-specific rules that must appear on every booking (e.g. "Time
                  into freezer must be ≤ 2 hrs from arrival — Kagome rule", or "Reversing camera
                  required — sharp corner into bay 4").
                </HelpTip>
              </div>
              <Textarea
                rows={2}
                data-testid="bay-booking-note-input"
                value={values.booking_note || ""}
                onChange={(e) => set("booking_note", e.target.value)}
                placeholder='e.g. "Time into freezer must be ≤ 2 hrs from arrival (Kagome rule)"'
              />
              <p className="text-[11px] text-slate-500">
                Surfaced as an amber banner at the top of the New / Edit
                booking modal whenever someone books this bay. Useful for
                client-specific or site-specific rules.
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle limits (optional)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Max length (m)</Label>
                <Input
                  type="number"
                  step="0.1"
                  data-testid="bay-max-length-input"
                  value={values.max_vehicle_length ?? ""}
                  onChange={(e) => set("max_vehicle_length", e.target.value)}
                  placeholder="e.g. 19.0"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Max height (m)</Label>
                <Input
                  type="number"
                  step="0.1"
                  data-testid="bay-max-height-input"
                  value={values.max_vehicle_height ?? ""}
                  onChange={(e) => set("max_vehicle_height", e.target.value)}
                  placeholder="e.g. 4.6"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Max GVM (kg)</Label>
                <Input
                  type="number"
                  step="1"
                  data-testid="bay-max-weight-input"
                  value={values.max_vehicle_weight ?? ""}
                  onChange={(e) => set("max_vehicle_weight", e.target.value)}
                  placeholder="e.g. 42500"
                />
              </div>
            </div>
          </section>

          <section className="space-y-3" data-testid="bay-hours-section">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bay hours &amp; open days</h3>
            <p className="text-[11px] text-slate-500">
              Narrows the site&apos;s hours for this bay only. Leave blank to use the site
              ({site?.operating_hours_start?.slice(0, 5) || "—"} – {site?.operating_hours_end?.slice(0, 5) || "—"}).
              Admins and dock managers can still override.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Opens at</Label>
                <AuTimeInput value={values.operating_hours_start?.slice(0, 5) || ""}
                  onChange={(e) => set("operating_hours_start", e.target.value)} data-testid="bay-hours-start-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Closes at</Label>
                <AuTimeInput value={values.operating_hours_end?.slice(0, 5) || ""}
                  onChange={(e) => set("operating_hours_end", e.target.value)} data-testid="bay-hours-end-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Open days</Label>
              <OpenDaysPicker value={values.open_days ?? null} onChange={(d) => set("open_days", d)}
                inheritLabel="Same as site" testid="bay-open-days" />
            </div>
            <div className="space-y-1.5">
              <Label>Daily breaks</Label>
              <DailyBreaksEditor value={values.daily_breaks ?? null} onChange={(b) => set("daily_breaks", b)}
                inheritLabel="Same as site" testid="bay-daily-breaks" />
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rules</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Max booking duration</Label>
                <Select
                  value={String(values.max_booking_hours ?? BAY_DEFAULTS.max_booking_hours)}
                  onValueChange={(v) => set("max_booking_hours", parseInt(v, 10))}
                >
                  <SelectTrigger data-testid="bay-max-hours-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAX_BOOKING_HOURS_OPTIONS.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {h} hour{h === 1 ? "" : "s"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sort order</Label>
                <Input
                  type="number"
                  data-testid="bay-sort-order-input"
                  value={values.sort_order ?? 0}
                  onChange={(e) => set("sort_order", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label>Slot duration override</Label>
                  <HelpTip id="bay-slot-duration">
                    The grid snap size for this bay. Leave as "Inherit from site" unless this bay
                    genuinely runs to a different rhythm (e.g. site is 30-min but chilled bays
                    move on 15-min turns).
                  </HelpTip>
                </div>
                <Select
                  value={String(values.slot_duration_minutes ?? "")}
                  onValueChange={(v) => set("slot_duration_minutes", v === "inherit" ? "" : parseInt(v, 10))}
                >
                  <SelectTrigger data-testid="bay-slot-override-select">
                    <SelectValue placeholder="Inherit from site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit from site</SelectItem>
                    {SLOT_DURATION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Booker minimum duration (min)</Label>
                <Input
                  type="number" min="0" step="5"
                  data-testid="bay-booker-min-duration-input"
                  value={values.booker_min_duration_minutes ?? ""}
                  onChange={(e) => set("booker_min_duration_minutes", e.target.value)}
                  placeholder="Inherit from site"
                />
                <p className="text-[11px] text-slate-500">Overrides site setting for this bay only. Blank = inherit.</p>
              </div>
              {clientBufferEnabled && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label>Turnaround buffer (minutes)</Label>
                    <HelpTip id="bay-buffer-override">
                      Leave blank to inherit the site default. Set 0 to disable
                      buffering at this bay even when the site has one. Max 180.
                    </HelpTip>
                  </div>
                  <Input
                    type="number" min="0" max="180" step="1"
                    data-testid="bay-buffer-minutes-input"
                    value={values.buffer_minutes ?? ""}
                    onChange={(e) => set("buffer_minutes", e.target.value)}
                    placeholder={`Inherit (${siteDefaultBuffer} min)`}
                  />
                  <p className="text-[11px] text-slate-500">Blank = inherit site default.</p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Card colour</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    className="w-12 h-9 p-1"
                    value={values.color || BAY_DEFAULTS.color}
                    onChange={(e) => set("color", e.target.value)}
                    data-testid="bay-color-picker"
                  />
                  <div className="flex gap-1 flex-wrap">
                    {BAY_COLOR_PALETTE.map((c) => (
                      <button
                        type="button"
                        key={c.value}
                        title={c.label}
                        onClick={() => set("color", c.value)}
                        className={`h-6 w-6 rounded-md ring-2 ring-offset-1 transition ${
                          values.color === c.value ? "ring-slate-900" : "ring-transparent"
                        }`}
                        style={{ backgroundColor: c.value }}
                        data-testid={`bay-color-swatch-${c.label.toLowerCase()}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <ToggleRow
                label="Requires approval"
                description="Bookings here go to pending_approval until a manager confirms"
                value={values.requires_approval}
                onChange={(v) => set("requires_approval", v)}
                testid="bay-requires-approval-switch"
                hint="Use for high-value or contract lanes where you don't want drivers turning up to bookings that haven't been triaged. External approved bookers will see 'Pending' in their view until an admin accepts."
              />
              <ToggleRow
                label="Allow multi-day bookings"
                description="Lets a single booking span more than one calendar day"
                value={values.allow_multiday}
                onChange={(v) => set("allow_multiday", v)}
                testid="bay-allow-multiday-switch"
              />
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Booking policy (bay overrides)
            </h3>
            <p className="text-[11px] text-slate-500 leading-snug -mt-1">
              Leave any field blank to inherit the site-wide default
              {site ? <> — site currently set to: {policyHints(site)}</> : null}.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Min booking duration (minutes)</Label>
                <Input
                  type="number" min="0" step="5"
                  data-testid="bay-min-booking-minutes-input"
                  value={values.min_booking_minutes ?? ""}
                  onChange={(e) => set("min_booking_minutes", e.target.value)}
                  placeholder={ph(site?.min_booking_minutes, "min")}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Min lead time (hours)</Label>
                <Input
                  type="number" min="0" step="1"
                  data-testid="bay-min-lead-hours-input"
                  value={values.min_lead_time_hours ?? ""}
                  onChange={(e) => set("min_lead_time_hours", e.target.value)}
                  placeholder={ph(site?.min_lead_time_hours, "h")}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Booking horizon (days ahead)</Label>
                <Input
                  type="number" min="1" step="1"
                  data-testid="bay-max-horizon-days-input"
                  value={values.max_horizon_days ?? ""}
                  onChange={(e) => set("max_horizon_days", e.target.value)}
                  placeholder={ph(site?.max_horizon_days, "d")}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Max concurrent bookings</Label>
                <Input
                  type="number" min="1" step="1"
                  data-testid="bay-max-concurrent-input"
                  value={values.max_concurrent_bookings ?? ""}
                  onChange={(e) => set("max_concurrent_bookings", e.target.value)}
                  placeholder={ph(site?.max_concurrent_bookings, "x", "1")}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>
                  Require check-in / check-out
                  {site && site.requires_check_in_out != null ? (
                    <span className="ml-2 text-[11px] text-slate-500 font-normal">
                      site default: {site.requires_check_in_out ? "Yes" : "No"}
                    </span>
                  ) : null}
                </Label>
                <Select
                  value={
                    values.requires_check_in_out === true ? "true"
                    : values.requires_check_in_out === false ? "false"
                    : "inherit"
                  }
                  onValueChange={(v) => set("requires_check_in_out", v)}
                >
                  <SelectTrigger data-testid="bay-requires-checkinout-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">
                      Inherit from site{site && site.requires_check_in_out != null ? ` (${site.requires_check_in_out ? "Yes" : "No"})` : ""}
                    </SelectItem>
                    <SelectItem value="true">Yes — driver must check in/out</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          <section className="flex items-center justify-between rounded-lg border bg-amber-50 border-amber-200 px-4 py-3">
            <div>
              <Label className="text-sm">On-demand bay (hidden from self-service)</Label>
              <p className="text-xs text-slate-600">
                When on, this bay is hidden from booker availability search and public
                booking links. Client admins, site admins and dock managers can still
                book it from the Dock Manager. Useful for overflow bays you keep in reserve.
              </p>
            </div>
            <Switch
              checked={!!values.is_on_demand}
              onCheckedChange={(v) => set("is_on_demand", v)}
              data-testid="bay-on-demand-switch"
            />
          </section>

          <section className="flex items-center justify-between rounded-lg border bg-slate-50 px-4 py-3">
            <div>
              <Label className="text-sm">Bay active</Label>
              <p className="text-xs text-slate-500">
                Inactive bays don&apos;t appear in booking pickers or the dock grid.
              </p>
            </div>
            <Switch
              checked={values.is_active}
              onCheckedChange={(v) => set("is_active", v)}
              data-testid="bay-active-switch"
            />
          </section>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="bay-save-btn">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create bay"}
            </Button>
          </DialogFooter>
        </form>
    </>
  );
}

function ToggleRow({ label, description, value, onChange, testid, hint }) {
  return (
    <div className="flex flex-1 items-start justify-between rounded-lg border bg-white px-3 py-2 gap-3">
      <div>
        <div className="flex items-center gap-1.5">
          <Label className="text-sm">{label}</Label>
          {hint ? <HelpTip>{hint}</HelpTip> : null}
        </div>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
